// Adaptador de los wires fuera de Control MDM a `readLockPhase`: la fase se deriva en un solo lugar.
import type { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import type { LoanCreditDeviceBlock } from '@/api/services/loan-credit/contract';
import { wireEnum } from '@/lib/wireEnum';
import type { CreditTone } from './creditFile/logic';
import {
    ACTIVE_UNCONFIRMED_LABEL,
    CUTOFF_PASSED_NOTE,
    CUTOFF_PASSED_SHORT_LABEL,
    CUTOFF_REFUTED_NOTE,
    CUTOFF_REFUTED_SHORT_LABEL,
    isActiveUnconfirmed,
    lockPhaseLabel,
    MDM_LOCK_PHASE_SHORT_LABEL,
    readLockPhase,
    type MdmLockPhase,
    type MdmLockPhaseFields,
} from './mdm/logic';

/** Lo mínimo que `readLockPhase` lee del bloque device; `mdmConfirmedAt` solo viaja en la flota. */
export type LockEvidence = Partial<
    Pick<
        LoanCreditDeviceBlock,
        | 'mdmStatus'
        | 'mdmConfirmed'
        | 'lockRefutedByProvider'
        | 'currentLockScheduledFor'
        | 'unlockedAt'
    >
> & { mdmConfirmedAt?: string };

/** El wire a los campos de `readLockPhase`; `null` sin estado. Ausente no es confirmado. */
function toPhaseFields(device: LockEvidence): MdmLockPhaseFields | null {
    if (device.mdmStatus === undefined) return null;
    return {
        mdmStatus: wireEnum<LoanDeviceMdmStatusEnum>(device.mdmStatus),
        mdmConfirmed: device.mdmConfirmed === true,
        lockRefutedByProvider: device.lockRefutedByProvider === true,
        currentLockScheduledFor: device.currentLockScheduledFor,
        unlockedAt: device.unlockedAt,
        mdmConfirmedAt: device.mdmConfirmedAt,
    };
}

/** En qué punto está el candado; `null` si no hay estado o nada lo matiza (ver `readLockPhase`). */
export function lockPhaseOf(
    device: LockEvidence,
    now: Date | string = new Date(),
): MdmLockPhase | null {
    const fields = toPhaseFields(device);
    return fields ? readLockPhase(fields, now) : null;
}

/** Lo que se sabe del candado: la fase, `activeUnconfirmed`, o `null` si nada matiza. La fase manda. */
export type LockReading = MdmLockPhase | 'activeUnconfirmed';

export function lockReadingOf(
    device: LockEvidence,
    now: Date | string = new Date(),
): LockReading | null {
    const fields = toPhaseFields(device);
    if (!fields) return null;
    const phase = readLockPhase(fields, now);
    if (phase) return phase;
    return isActiveUnconfirmed(fields) ? 'activeUnconfirmed' : null;
}

/** Dónde cae el equipo en los filtros de bloqueo: la misma lectura que la celda. */
export type LockFilterBucket = 'locked' | 'unlocked';

/** «locked» solo con candado confirmado; lo que espera al proveedor, lo desmintió o venció, `null`. */
export function lockFilterBucketOf(
    device: LockEvidence,
    now: Date | string = new Date(),
): LockFilterBucket | null {
    if (device.mdmStatus === undefined) return null;
    const reading = lockReadingOf(device, now);
    if (reading === null || reading === 'scheduled') return 'unlocked';
    return reading === 'locked' ? 'locked' : null;
}

/** La pastilla corta de cada lectura que no es el candado confirmado; `title` lleva la frase del panel. */
export const LOCK_READING_CELL: Record<
    Exclude<LockReading, 'locked'>,
    { label: string; tone: CreditTone; title: string }
> = {
    refuted: { label: 'Desmentido', tone: 'danger', title: MDM_LOCK_PHASE_SHORT_LABEL.refuted },
    unconfirmed: {
        label: 'Sin confirmar',
        tone: 'warning',
        title: lockPhaseLabel('unconfirmed', undefined),
    },
    scheduled: {
        label: 'Programado',
        tone: 'warning',
        title: MDM_LOCK_PHASE_SHORT_LABEL.scheduled,
    },
    activeUnconfirmed: {
        label: 'Activo sin confirmar',
        tone: 'warning',
        title: ACTIVE_UNCONFIRMED_LABEL,
    },
    cutoffPassed: {
        label: CUTOFF_PASSED_SHORT_LABEL,
        tone: 'warning',
        title: CUTOFF_PASSED_NOTE,
    },
    cutoffRefuted: {
        label: CUTOFF_REFUTED_SHORT_LABEL,
        tone: 'danger',
        title: CUTOFF_REFUTED_NOTE,
    },
};
